import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/types/supabase-types'
import { NotificationConfiguration, NotificationRecipient } from '@/integrations/supabase/types'
import { UserRole, NotificationType } from '@/types/enums'

// Type for basic notification from database
type Notification = Database['ShiftSage']['Tables']['notifications']['Row']
type NotificationInsert = Database['ShiftSage']['Tables']['notifications']['Insert']

// Extended type that includes metadata
interface NotificationWithMetadata extends Omit<NotificationInsert, 'id'> {
  metadata?: Record<string, unknown>
  type?: NotificationType
}

/**
 * Service for handling notifications
 */
export class NotificationService {
  /**
   * Creates a new notification
   */
  static async createNotification(notification: NotificationWithMetadata): Promise<Notification | null> {
    const { error, data } = await supabase
      .from('notifications')
      .insert(notification as any)
      .select()
      .single()

    if (error) {
      return null
    }

    return data
  }

  /**
   * Marks a notification as read
   */
  static async markAsRead(notificationId: string): Promise<boolean> {
    const { error } = await (supabase as any)
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)

    if (error) {
      return false
    }

    return true
  }

  /**
   * Deletes a notification by ID
   */
  static async deleteNotification(notificationId: string): Promise<boolean> {
    const { error } = await (supabase as any)
      .from('notifications')
      .delete()
      .eq('id', notificationId)

    if (error) {
      return false
    }

    return true
  }

  /**
   * Gets unread notifications for a user
   */
  static async getUnreadNotifications(userId: string): Promise<Notification[]> {
    const { data, error } = await (supabase as any)
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('read', false)
      .order('created_at', { ascending: false })

    if (error) {
      return []
    }

    return data || []
  }

  /**
   * Gets all notifications for a user
   */
  static async getAllNotifications(userId: string): Promise<Notification[]> {
    const { data, error } = await (supabase as any)
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      return []
    }

    return data || []
  }

  /**
   * Gets notification configuration for a company
   */
  static async getNotificationConfig(companyId: string): Promise<NotificationConfiguration | null> {
    const { data, error } = await (supabase as any)
      .from('notification_configurations')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true)
      .single()

    if (error) {
      return null
    }

    return data
  }

  /**
   * Gets notification recipients for a configuration
   */
  static async getNotificationRecipients(configId: string): Promise<NotificationRecipient[]> {
    const { data, error } = await (supabase as any)
      .from('notification_recipients')
      .select('*')
      .eq('configuration_id', configId)

    if (error) {
      return []
    }

    return data || []
  }

  /**
   * Sends threshold notification to configured recipients
   */
  static async sendThresholdNotification(params: {
    companyId: string
    employeeId: string
    employeeName: string
    approverId: string
    approverName: string
    month: string
    totalHours: number
    threshold: number
  }): Promise<void> {
    const { companyId, employeeId, employeeName, approverId, approverName, month, totalHours, threshold } = params

    // Get notification configuration
    const config = await this.getNotificationConfig(companyId)
    if (!config) return

    // Get recipients
    const recipients = await this.getNotificationRecipients(config.id)

    // Create notification message
    const message = `${approverName} approved ${employeeName} to work ${totalHours} hours in ${month}, exceeding the threshold of ${threshold} hours.`

    // Prepare metadata
    const metadataObj = {
      type: NotificationType.SYSTEM_UPDATE,
      employee_id: employeeId,
      approver_id: approverId,
      month,
      total_hours: totalHours,
      threshold
    };

    // Send to each recipient
    for (const recipient of recipients) {
      await this.createNotification({
        user_id: recipient.recipient_id,
        title: 'Overtime Threshold Exceeded',
        message,
        metadata: metadataObj,
        read: false,
        type: NotificationType.SYSTEM_UPDATE
      })
    }

    // Always send to super admin
    const { data: profile, error: profileError } = await (supabase as any)
      .from('profiles')
      .select('id, email, role')
      .eq('role', UserRole.SUPER_ADMIN)
      .single();

    if (profileError) {
      return;
    }

    if (profile) {
      await this.createNotification({
        user_id: (profile as any).id,
        title: 'Overtime Threshold Exceeded',
        message,
        metadata: metadataObj,
        read: false,
        type: NotificationType.SYSTEM_UPDATE
      })
    }
  }
} 